import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as FitWidthIcon,
  FitScreen as FitPageIcon,
  ViewSidebar as ThumbnailsIcon,
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFViewerProps } from '../../../types/viewer.types';
import { environment } from '../../../../environments/environment';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 5.0;
const THUMB_SCALE = 0.15;

const PDFViewer: React.FC<PDFViewerProps> = ({
  contentId,
  teamId,
  metadata,
  onError,
  onLoad,
  className,
  style,
  isConverted = false,
  convertedUrl,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [fitWidthScale, setFitWidthScale] = useState(1);
  const [fitPageScale, setFitPageScale] = useState(1);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const renderTaskRefs = useRef<(pdfjsLib.RenderTask | null)[]>([]);
  const thumbCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const thumbRenderTaskRefs = useRef<(pdfjsLib.RenderTask | null)[]>([]);
  const pageBoxRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);

  // Fetch PDF and hand ArrayBuffer directly to PDF.js
  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError('');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);

        let downloadUrl: string;
        let needsAuth = true;
        let token: string | null = null;

        if (convertedUrl && isConverted) {
          downloadUrl = convertedUrl;
          needsAuth = false;
        } else if (metadata.presignedUrl) {
          downloadUrl = metadata.presignedUrl;
          needsAuth = false;
        } else {
          token = localStorage.getItem('accessToken');
          if (!token) throw new Error('No authentication token found');
          downloadUrl = `${environment.apiUrl}/content/${contentId}/download${teamId ? `?team_id=${teamId}` : ''}`;
        }

        const fetchOptions: RequestInit = { signal: controller.signal };
        if (needsAuth && token) {
          fetchOptions.headers = { 'Authorization': `Bearer ${token}` };
        }

        const response = await fetch(downloadUrl, fetchOptions).finally(() => {
          clearTimeout(timeoutId);
        });

        if (!response.ok) {
          throw new Error(
            response.status === 401
              ? 'Authentication failed. Please log in again.'
              : `Failed to load PDF: ${response.statusText}`
          );
        }

        const arrayBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);

        if (bytes[0] !== 0x25 || bytes[1] !== 0x50 || bytes[2] !== 0x44 || bytes[3] !== 0x46) {
          if (bytes[0] === 0x7B) {
            try {
              const jsonData = JSON.parse(new TextDecoder().decode(arrayBuffer));
              throw new Error(jsonData.message || jsonData.error || 'Server returned an error');
            } catch (e) {
              if (e instanceof Error && !e.message.includes('JSON')) throw e;
            }
          }
          throw new Error('Received data is not a valid PDF file');
        }

        if (cancelled) return;

        const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        if (cancelled) {
          doc.destroy();
          return;
        }

        pdfDocRef.current = doc;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
      } catch (err) {
        if (cancelled) return;
        const errorMessage = err instanceof Error ? err.message : 'Failed to load PDF';
        setError(errorMessage);
        setLoading(false);
        onError?.({ code: 'PDF_LOAD_ERROR', message: errorMessage, retryable: true });
      }
    };

    loadPdf();
    return () => { cancelled = true; };
  }, [contentId, metadata, isConverted, convertedUrl, onError]);

  // Destroy PDF document on unmount
  useEffect(() => {
    return () => { pdfDocRef.current?.destroy(); };
  }, []);

  // Calculate fit-width and fit-page scales once PDF and container are ready
  const calculateFitScales = useCallback(async () => {
    if (!pdfDoc || !scrollContainerRef.current) return;
    const page = await pdfDoc.getPage(1);
    const naturalViewport = page.getViewport({ scale: 1 });
    const availableWidth = scrollContainerRef.current.clientWidth - 48;
    const availableHeight = scrollContainerRef.current.clientHeight - 48;
    const widthScale = availableWidth / naturalViewport.width;
    const heightScale = availableHeight / naturalViewport.height;
    const newFitWidth = Math.min(Math.max(widthScale, ZOOM_MIN), ZOOM_MAX);
    const newFitPage = Math.min(Math.max(Math.min(widthScale, heightScale), ZOOM_MIN), ZOOM_MAX);
    setFitWidthScale(newFitWidth);
    setFitPageScale(newFitPage);
    setScale(newFitPage); // default to fit-page
  }, [pdfDoc]);

  useEffect(() => {
    calculateFitScales();
  }, [calculateFitScales]);

  // Re-render all pages when pdfDoc or scale changes
  useEffect(() => {
    if (!pdfDoc || numPages === 0 || scale === 0) return;

    const renderPage = async (pageNum: number) => {
      const canvas = canvasRefs.current[pageNum - 1];
      if (!canvas) return;

      renderTaskRefs.current[pageNum - 1]?.cancel();
      renderTaskRefs.current[pageNum - 1] = null;

      try {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const task = page.render({ canvas, canvasContext: ctx, viewport });
        renderTaskRefs.current[pageNum - 1] = task;
        await task.promise;
        renderTaskRefs.current[pageNum - 1] = null;
        if (pageNum === 1) {
          setLoading(false);
          onLoad?.();
        }
      } catch (err: any) {
        if (err?.name === 'RenderingCancelledException') return;
        console.error(`PDFViewer: error rendering page ${pageNum}:`, err);
      }
    };

    for (let i = 1; i <= numPages; i++) renderPage(i);

    return () => {
      renderTaskRefs.current.forEach(task => task?.cancel());
      renderTaskRefs.current = new Array(numPages).fill(null);
    };
  }, [pdfDoc, scale, numPages, onLoad]);

  // Render thumbnails when the panel is opened (canvases mount after showThumbnails becomes true)
  useEffect(() => {
    if (!pdfDoc || numPages === 0 || !showThumbnails) return;

    const renderThumb = async (pageNum: number) => {
      const canvas = thumbCanvasRefs.current[pageNum - 1];
      if (!canvas) return;
      thumbRenderTaskRefs.current[pageNum - 1]?.cancel();
      thumbRenderTaskRefs.current[pageNum - 1] = null;
      try {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: THUMB_SCALE });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const task = page.render({ canvas, canvasContext: ctx, viewport });
        thumbRenderTaskRefs.current[pageNum - 1] = task;
        await task.promise;
        thumbRenderTaskRefs.current[pageNum - 1] = null;
      } catch (err: any) {
        if (err?.name === 'RenderingCancelledException') return;
      }
    };

    for (let i = 1; i <= numPages; i++) renderThumb(i);

    return () => {
      thumbRenderTaskRefs.current.forEach(task => task?.cancel());
      thumbRenderTaskRefs.current = new Array(numPages).fill(null);
    };
  }, [pdfDoc, numPages, showThumbnails]);

  // Track which page is currently in view via IntersectionObserver
  useEffect(() => {
    if (!scrollContainerRef.current || numPages === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (mostVisible) {
          const pageNum = parseInt(mostVisible.target.getAttribute('data-page') || '1', 10);
          setCurrentPage(pageNum);
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );

    pageBoxRefs.current.forEach(box => { if (box) observer.observe(box); });
    return () => observer.disconnect();
  }, [numPages, pdfDoc]);

  const scrollToPage = (pageNum: number) => {
    const box = pageBoxRefs.current[pageNum - 1];
    if (box) box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleZoomIn = () =>
    setScale(prev => parseFloat(Math.min(prev + ZOOM_STEP, ZOOM_MAX).toFixed(2)));
  const handleZoomOut = () =>
    setScale(prev => parseFloat(Math.max(prev - ZOOM_STEP, ZOOM_MIN).toFixed(2)));
  const handleFitPage = () => setScale(fitPageScale);
  const handleFitWidth = () => setScale(fitWidthScale);

  return (
    <Box
      className={className}
      style={style}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}
    >
      {/* Toolbar */}
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          gap: 0.5,
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Tooltip title={showThumbnails ? 'Hide page panel' : 'Show page panel'}>
          <IconButton
            size="small"
            onClick={() => setShowThumbnails(p => !p)}
            color={showThumbnails ? 'primary' : 'default'}
            disabled={loading}
          >
            <ThumbnailsIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Zoom out">
          <span>
            <IconButton size="small" onClick={handleZoomOut} disabled={scale <= ZOOM_MIN || loading}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Chip label={`${Math.round(scale * 100)}%`} size="small" sx={{ minWidth: 60 }} />
        <Tooltip title="Zoom in">
          <span>
            <IconButton size="small" onClick={handleZoomIn} disabled={scale >= ZOOM_MAX || loading}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit page">
          <span>
            <IconButton size="small" onClick={handleFitPage} disabled={loading}>
              <FitPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit width">
          <span>
            <IconButton size="small" onClick={handleFitWidth} disabled={loading}>
              <FitWidthIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {numPages > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {currentPage} / {numPages}
            </Typography>
          </>
        )}
      </Paper>

      {/* Body: optional thumbnail panel + main canvas area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Thumbnail sidebar */}
        {showThumbnails && (
          <Box
            sx={{
              width: 148,
              flexShrink: 0,
              overflow: 'auto',
              bgcolor: '#3a3a3a',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 1,
              gap: 1,
              borderRight: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
              <Box
                key={pageNum}
                onClick={() => scrollToPage(pageNum)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.25,
                  cursor: 'pointer',
                  p: 0.5,
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: currentPage === pageNum ? 'primary.main' : 'transparent',
                  '&:hover': { borderColor: currentPage === pageNum ? 'primary.main' : 'rgba(255,255,255,0.3)' },
                }}
              >
                <Box sx={{ lineHeight: 0, boxShadow: 2 }}>
                  <canvas
                    ref={el => { thumbCanvasRefs.current[pageNum - 1] = el; }}
                    style={{ display: 'block', maxWidth: 120 }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: currentPage === pageNum ? 'primary.light' : 'grey.400', fontSize: 10 }}>
                  {pageNum}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Main scrollable canvas area */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: '#525659',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 3,
            gap: 2,
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" sx={{ mt: 2, color: 'grey.400' }}>
                Loading PDF...
              </Typography>
            </Box>
          )}

          {error && !loading && (
            <Alert severity="error" sx={{ maxWidth: 440, mx: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Failed to Load PDF</Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {!error && pdfDoc && Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
            <Box
              key={pageNum}
              data-page={pageNum}
              ref={el => { pageBoxRefs.current[pageNum - 1] = el as HTMLDivElement; }}
              sx={{ boxShadow: 4, lineHeight: 0, bgcolor: 'white' }}
            >
              <canvas
                ref={el => { canvasRefs.current[pageNum - 1] = el; }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PDFViewer;
